import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ApiError,
  api,
  type AvailabilitySlot,
  type GuestSummary,
} from '../lib/api.js';
import { useVenueId } from '../lib/session.js';
import { useVenue } from '../hooks/useVenue.js';

/**
 * Create a booking.
 *
 * Two details matter more than the form itself:
 *
 *  - GUEST SEARCH IS FIRST. A returning guest's allergies, VIP level and
 *    seating preference already exist; retyping them creates a duplicate and
 *    loses the history. The "new guest" fields only appear once a host has
 *    looked and not found them.
 *
 *  - AVAILABILITY IS LIVE. Slots come from the seating engine for this exact
 *    party size, and unbookable ones are shown with their reason rather than
 *    hidden — "fully booked" and "past last seating" are different answers to
 *    give a guest on the phone.
 */
export function BookingDrawer({
  serviceDate,
  onClose,
  onSaved,
}: {
  serviceDate: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const venueId = useVenueId();
  const venue = useVenue();

  const [partySize, setPartySize] = useState(2);
  const [search, setSearch] = useState('');
  const [guest, setGuest] = useState<GuestSummary | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [slot, setSlot] = useState<AvailabilitySlot | null>(null);
  const [occasion, setOccasion] = useState('');
  const [requests, setRequests] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Close on Escape. A drawer that traps a host during a rush is a drawer they
  // learn to avoid opening.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const guests = useQuery({
    queryKey: ['guest-search', venueId, search],
    enabled: search.trim().length >= 2,
    queryFn: () =>
      api.get<{ guests: GuestSummary[] }>('/api/v1/guests', {
        venue_id: venueId,
        q: search.trim(),
      }),
  });

  const availability = useQuery({
    queryKey: ['staff-availability', venue?.slug, serviceDate, partySize],
    enabled: Boolean(venue && serviceDate),
    queryFn: () =>
      api.get<{ slots: AvailabilitySlot[] }>(
        `/api/v1/public/venues/${venue!.slug}/availability`,
        { date: serviceDate, party_size: partySize },
      ),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post(
        '/api/v1/reservations',
        {
          venue_id: venueId,
          starts_at: slot!.starts_at,
          party_size: partySize,
          source: 'phone',
          ...(guest ? { guest_id: guest.id } : {}),
          ...(guest ? {} : { first_name: firstName || undefined, last_name: lastName || undefined, phone: phone || undefined }),
          ...(occasion ? { occasion } : {}),
          ...(requests ? { special_requests: requests } : {}),
        },
        // A host double-tapping Save on a slow connection must not produce two
        // bookings for one party.
        { idempotencyKey: `drawer-${venueId}-${slot?.starts_at}-${phone || guest?.id}` },
      ),
    onSuccess: onSaved,
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : t('errors.generic')),
  });

  const bookable = useMemo(
    () => (availability.data?.slots ?? []).filter((s) => s.bookable),
    [availability.data],
  );

  const canSave = Boolean(slot) && (Boolean(guest) || firstName.trim().length > 0);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30" role="dialog" aria-modal="true">
      <div className="flex h-full w-full max-w-lg flex-col gap-4 overflow-y-auto bg-white p-5 shadow-xl">
        <header className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t('drawer.create')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-slate-500 hover:bg-slate-100"
          >
            {t('common.close')}
          </button>
        </header>

        {error ? (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {error}
          </p>
        ) : null}

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('drawer.partySize')}</span>
          <input
            type="number"
            min={1}
            max={50}
            value={partySize}
            onChange={(event) => {
              setPartySize(Number(event.target.value) || 1);
              // The chosen slot may not fit the new party size, and silently
              // keeping it would book a six-top into a two-top window.
              setSlot(null);
            }}
            className="w-24 rounded-md border border-slate-200 px-2 py-1.5 font-mono"
          />
        </label>

        <section className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t('drawer.searchGuest')}</span>
            <input
              type="search"
              value={search}
              placeholder={t('drawer.searchHint')}
              onChange={(event) => {
                setSearch(event.target.value);
                setGuest(null);
              }}
              className="rounded-md border border-slate-200 px-2 py-1.5"
            />
          </label>

          {guests.data?.guests.length ? (
            <ul className="max-h-40 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200">
              {guests.data.guests.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setGuest(row);
                      setSearch(
                        [row.first_name, row.last_name].filter(Boolean).join(' ') ||
                          row.name_ar ||
                          '',
                      );
                    }}
                    className={[
                      'flex w-full items-center justify-between px-3 py-2 text-start text-sm hover:bg-brand-teal-light',
                      guest?.id === row.id ? 'bg-brand-teal-light' : '',
                    ].join(' ')}
                  >
                    <span>
                      {[row.first_name, row.last_name].filter(Boolean).join(' ') || row.name_ar}
                      {row.allergies ? (
                        <span className="ms-2 rounded bg-red-50 px-1 text-xs text-red-700">
                          {row.allergies}
                        </span>
                      ) : null}
                    </span>
                    <span className="font-mono text-xs text-slate-500" data-numeric>
                      {`${row.total_visits}`}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {/* Only offered once a host has searched and not found them. */}
          {!guest && search.trim().length >= 2 && guests.data?.guests.length === 0 ? (
            <div className="grid gap-2 rounded-md border border-dashed border-slate-300 p-3 sm:grid-cols-2">
              <p className="col-span-full text-xs text-slate-500">{t('drawer.newGuest')}</p>
              <input
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                placeholder={t('drawer.firstName')}
                className="rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              />
              <input
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                placeholder={t('drawer.lastName')}
                className="rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              />
              <input
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                placeholder={t('drawer.phone')}
                inputMode="tel"
                className="col-span-full rounded-md border border-slate-200 px-2 py-1.5 font-mono text-sm"
              />
            </div>
          ) : null}
        </section>

        <section className="flex flex-col gap-2">
          <span className="text-sm font-medium">{t('drawer.chooseSlot')}</span>
          {availability.isLoading ? (
            <p className="text-sm text-slate-500">{t('app.loading')}</p>
          ) : null}

          {bookable.length === 0 && !availability.isLoading ? (
            <p className="text-sm text-slate-500">{t('drawer.noSlots')}</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {bookable.map((option) => (
                <button
                  key={option.starts_at}
                  type="button"
                  onClick={() => setSlot(option)}
                  className={[
                    'rounded-md border px-2.5 py-1 font-mono text-sm',
                    slot?.starts_at === option.starts_at
                      ? 'border-brand-teal bg-brand-teal text-white'
                      : 'border-slate-200 hover:bg-brand-teal-light',
                    // "Only a few tables left" is worth showing a host taking a
                    // phone booking, so they can say it out loud.
                    option.confidence === 'limited' ? 'ring-1 ring-amber-300' : '',
                  ].join(' ')}
                  data-numeric
                >
                  {option.clock}
                </button>
              ))}
            </div>
          )}
        </section>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('drawer.occasion')}</span>
          <input
            value={occasion}
            onChange={(event) => setOccasion(event.target.value)}
            className="rounded-md border border-slate-200 px-2 py-1.5"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('drawer.requests')}</span>
          <textarea
            value={requests}
            onChange={(event) => setRequests(event.target.value)}
            rows={3}
            className="rounded-md border border-slate-200 px-2 py-1.5"
          />
        </label>

        <div className="mt-auto flex gap-2">
          <button
            type="button"
            disabled={!canSave || create.isPending}
            onClick={() => create.mutate()}
            className="rounded-md bg-brand-teal px-4 py-2 text-sm font-medium text-white hover:bg-brand-teal-dark disabled:opacity-40"
          >
            {t('drawer.save')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-200 px-4 py-2 text-sm"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
